import { authedFetch } from '@/lib/authed-fetch';
import type { Database } from '@sixam/shared/database.types';

export type ReportSubject = Database['public']['Enums']['report_subject'];
export type ReportReason = Database['public']['Enums']['report_reason'];

export type ReportReasonOption = Readonly<{ label: string; value: ReportReason }>;

/** Phrased as a member would describe it, not as a policy document would. */
export const REPORT_REASONS: readonly ReportReasonOption[] = [
  { label: 'Hateful or offensive content', value: 'objectionable_content' },
  { label: 'Harassment or bullying', value: 'harassment' },
  { label: 'Spam or a scam', value: 'spam' },
  { label: 'Pretending to be someone else', value: 'impersonation' },
  { label: 'Something else', value: 'other' },
];

async function parse<T>(response: Response, fallback: string): Promise<T> {
  const payload = (await response.json().catch(() => null)) as ({ error?: string } & T) | null;
  if (!response.ok) {
    throw new Error(payload?.error ?? fallback);
  }
  return payload as T;
}

export async function reportContent(input: {
  details?: string;
  reason: ReportReason;
  subjectId: string;
  subjectType: ReportSubject;
}): Promise<void> {
  const response = await authedFetch('/api/moderation/reports', {
    body: JSON.stringify(input),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  await parse(response, 'Could not send the report.');
}

export async function blockMember(userId: string, reason?: string): Promise<void> {
  const response = await authedFetch('/api/moderation/blocks', {
    body: JSON.stringify({ reason, userId }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  await parse(response, 'Could not block this member.');
}

export async function unblockMember(userId: string): Promise<void> {
  const response = await authedFetch('/api/moderation/blocks', {
    body: JSON.stringify({ userId }),
    headers: { 'Content-Type': 'application/json' },
    method: 'DELETE',
  });
  await parse(response, 'Could not unblock this member.');
}

export async function fetchBlockedUserIds(): Promise<readonly string[]> {
  const response = await authedFetch('/api/moderation/blocks');
  const payload = await parse<{ blockedUserIds: string[] }>(
    response,
    'Could not load blocked members.'
  );
  return payload.blockedUserIds;
}
